// Pure helpers for the reporting chat composer (reporting_ask_controller.js): the
// new-conversation shortcut and pasted images. Kept DOM-free so they're unit tested
// in test/javascript/reporting_ask_test.mjs.

// Must match PartnerReportingChat::Images on the server.
export const IMAGE_LIMIT = 4
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"]

// ⌘N / Ctrl+N where the browser lets a page have it (Safari, installed-app windows).
// Chrome and Firefox keep it for "new window", so ⌘⇧O / Ctrl+Shift+O (ChatGPT's
// shortcut) works everywhere and is the one the button shows.
export function isNewConversationShortcut(event) {
  if (!(event.metaKey || event.ctrlKey) || event.altKey || event.isComposing) return false
  const key = event.key?.toLowerCase()
  return (key === "n" && !event.shiftKey) || (key === "o" && event.shiftKey)
}

// "⌘⇧O" on a Mac, "Ctrl+Shift+O" elsewhere. `keys` like "shift+o" or "v".
export function shortcutLabel(platform, keys) {
  const mac = /Mac|iPhone|iPad|iPod/i.test(platform || "")
  const parts = keys.split("+").map(key => key === "shift" ? (mac ? "⇧" : "Shift") : key.toUpperCase())
  return mac ? ["⌘", ...parts].join("") : ["Ctrl", ...parts].join("+")
}

// Image files on a paste event's clipboard. Screenshots arrive as a file item.
// Office apps put a picture of the selection next to its text; the text wins, so
// pasting spreadsheet cells still pastes numbers.
export function pastedImageFiles(clipboardData) {
  if (!clipboardData) return []
  if (clipboardData.getData?.("text/plain")?.trim()) return []
  const fromItems = [...(clipboardData.items || [])]
    .filter(item => item.kind === "file" && item.type?.startsWith("image/"))
    .map(item => item.getAsFile())
    .filter(Boolean)
  if (fromItems.length) return fromItems
  return [...(clipboardData.files || [])].filter(file => file.type?.startsWith("image/"))
}

// Why a pasted file can't be attached, or null when it can.
export function imageProblem(file, attachedCount) {
  if (attachedCount >= IMAGE_LIMIT) return `You can attach up to ${IMAGE_LIMIT} images.`
  if (!IMAGE_TYPES.includes(file.type)) return "Paste a PNG, JPEG, GIF or WebP image."
  if (file.size > IMAGE_MAX_BYTES) return "Images can be up to 10 MB."
  return null
}

// A pasted screenshot is usually named "image.png"; give it a readable, unique name.
export function pastedImageName(file, now = new Date()) {
  const name = file.name || ""
  if (name && !/^image\.\w+$/i.test(name)) return name
  const extension = (file.type || "image/png").split("/")[1].replace("jpeg", "jpg")
  const pad = value => String(value).padStart(2, "0")
  return `Pasted image ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}.${extension}`
}

// Keeps the start and the end with the extension: "Screenshot 2026-10-…2 AM.png".
export function truncateFilename(name, max = 30) {
  if (!name || name.length <= max) return name || ""
  const dot = name.lastIndexOf(".")
  const extension = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : ""
  const keepEnd = Math.min(extension.length + 6, max - 4)
  // Don't let the ellipsis touch a dot or space ("2026-1….55.png" reads as a doubled ellipsis).
  const head = name.slice(0, max - keepEnd - 1).replace(/[.\s]+$/, "")
  const tail = name.slice(name.length - keepEnd).replace(/^[.\s]+/, "")
  return `${head}…${tail}`
}
